import { useCallback, useMemo } from "react";
import { useLocation, useSearch } from "wouter";

export type Primitivo = string | number | boolean;

export type Permitidos<T extends Record<string, Primitivo>> = {
  [K in keyof T]?: readonly T[K][];
};

/**
 * Lê o estado da lista a partir de uma query string.
 *
 * `padrao` define TRÊS coisas de uma vez: quais chaves existem, de que tipo é
 * cada uma (pelo `typeof` do valor padrão) e o que fica FORA da URL — valor
 * igual ao padrão nunca é serializado, então `/cacho/vendas` limpo é a visão
 * padrão e o `?` só aparece quando alguém mexeu num filtro.
 *
 * `permitidos` limita chaves categóricas: valor fora da lista cai no padrão em
 * vez de quebrar a tela. É a proteção contra link velho — `?periodo=banana`
 * (preset renomeado, link colado do WhatsApp em junho) abre no padrão, não em
 * tela branca.
 */
export function lerEstado<T extends Record<string, Primitivo>>(
  search: string,
  padrao: T,
  permitidos?: Permitidos<T>,
): T {
  const p = new URLSearchParams(search);
  const saida: Record<string, Primitivo> = { ...padrao };
  for (const chave of Object.keys(padrao)) {
    const bruto = p.get(chave);
    if (bruto === null) continue;
    const def = padrao[chave];
    let valor: Primitivo;
    if (typeof def === "number") {
      const n = Number(bruto);
      if (!Number.isFinite(n)) continue; // número inválido → padrão
      valor = n;
    } else if (typeof def === "boolean") {
      valor = bruto === "1" || bruto === "true";
    } else {
      valor = bruto;
    }
    const lista = permitidos?.[chave as keyof T] as readonly Primitivo[] | undefined;
    if (lista && !lista.includes(valor)) continue; // fora do enum → padrão
    saida[chave] = valor;
  }
  return saida as T;
}

/**
 * Serializa só o que difere do padrão, PRESERVANDO as chaves que não são deste
 * `padrao`. Sem isso, duas telas compostas se pisam: em Manejo · Programa a
 * página é dona de `aba` e a aba é dona de `q`, e digitar na busca apagaria o
 * `?aba=setores` — o usuário seria devolvido à primeira aba a cada tecla. Também
 * é o que deixa um parâmetro de ENTRADA (`?editar=telefone`) sobreviver a um
 * clique no filtro.
 *
 * A ordem é a das chaves de `padrao` numa URL limpa — estável, para a URL não
 * mudar de forma a cada clique.
 */
export function serializar<T extends Record<string, Primitivo>>(
  estado: T,
  padrao: T,
  searchAtual = "",
): string {
  const p = new URLSearchParams(searchAtual);
  for (const chave of Object.keys(padrao)) {
    const v = estado[chave];
    if (v === undefined || v === "" || v === padrao[chave]) p.delete(chave);
    else p.set(chave, typeof v === "boolean" ? (v ? "1" : "0") : String(v));
  }
  return p.toString();
}

/** Tira da query APENAS as chaves deste `padrao` — ver `serializar`. */
export function limparChaves<T extends Record<string, Primitivo>>(padrao: T, searchAtual: string): string {
  const p = new URLSearchParams(searchAtual);
  for (const chave of Object.keys(padrao)) p.delete(chave);
  return p.toString();
}

/** Quantas chaves estão fora do padrão — alimenta o "Filtros · 2" do mobile. */
export function contarAtivos<T extends Record<string, Primitivo>>(estado: T, padrao: T): number {
  return Object.keys(padrao).filter(k => estado[k] !== padrao[k] && estado[k] !== "").length;
}

/**
 * O estado da lista mora na URL: filtros, busca, aba, visão e página são LIDOS
 * de `useSearch()` e ESCRITOS com `navigate(..., { replace: true })`. Não
 * existe `useState` para filtro.
 *
 * De graça vêm três coisas que nenhum `sessionStorage`/contexto dá: "Voltar"
 * (botão, gesto do celular) restaura a lista porque a entrada do histórico já
 * carrega os filtros; reload e troca de módulo não apagam nada; e o recorte
 * vira um link que se manda no WhatsApp.
 *
 * ⚠️ `padrao` e `permitidos` PRECISAM ser constantes de módulo (declaradas fora
 * do componente): são deps de `useMemo`/`useCallback`, e um objeto literal
 * recriado a cada render refaz o estado a cada render.
 *
 * Paginação: o hook não adivinha. Quem tem `pagina` reseta explicitamente ao
 * mudar outro filtro — `definir({ setor: x, pagina: 1 })`.
 */
export function useEstadoNaUrl<T extends Record<string, Primitivo>>(
  padrao: T,
  permitidos?: Permitidos<T>,
) {
  const search = useSearch();
  const [caminho, navegar] = useLocation();

  const estado = useMemo(
    () => lerEstado(search, padrao, permitidos),
    [search, padrao, permitidos],
  );

  /** Mescla e grava com `replace` — mexer em filtro não cria entrada no histórico. */
  const definir = useCallback(
    (parcial: Partial<T>) => {
      const qs = serializar({ ...estado, ...parcial }, padrao, search);
      navegar(qs ? `${caminho}?${qs}` : caminho, { replace: true });
    },
    [estado, padrao, search, caminho, navegar],
  );

  /** Volta as chaves DESTE padrão ao default, sem mexer no que é de outro hook. */
  const limpar = useCallback(
    () => {
      const qs = limparChaves(padrao, search);
      navegar(qs ? `${caminho}?${qs}` : caminho, { replace: true });
    },
    [padrao, search, caminho, navegar],
  );

  const ativos = useMemo(() => contarAtivos(estado, padrao), [estado, padrao]);

  return { estado, definir, limpar, ativos };
}
